import { useTranslation } from "react-i18next";
import { NewTagData, Tag } from "../../../models/Tags";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { useEffect, useState } from "react";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { Toast } from "../../Toast";
import { Modal } from "../../Modal";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../LoadingMask";
import { createTag, updateTag } from "../../../state/tags/action";
import { RoleEnum } from "../../../enums/Role";
import { ReactiveFormInput } from "../../form/ReactiveFormInput";
import { ReactiveFormRadioButtonSelect } from "../../form/ReactiveFormRadioButtonSelect";
import { User } from "../../../models/User";

interface Props {
  info: {
    show: boolean;
    update: boolean;
    tag?: Tag;
    currentUser: User;
  };
  onClose: () => void;
}

export const AgencyTagModal: React.FC<Props> = ({
  info,
  onClose,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const isLoading = useSelector((state: RootState) => state.tags.isLoading);
    const error = useSelector((state: any) => state.tags.error);
    const [showTagError, setShowTagError] = useState(false);

    const methods = useForm<any>({
        defaultValues: {},
    });
    const { handleSubmit, control, getValues, reset } = methods;

    useEffect(() => {
        info.tag ? reset(info.tag) : reset({});
    }, [info.tag, reset]);

    useEffect(() => {
        if (showTagError) {
            Toast(
                t(error?.reason ?? "SOMETHING_WENT_WRONG"),
                t(error?.cause_info)
            );
        }
    }, [showTagError, error, t]);

    const onSubmitInvalid: SubmitErrorHandler<NewTagData> = (data) => {
        toast(t("PLEASE_ENTER_ALL_TAG_REQUIRED_FIELDS"));
    }
    
    const onSubmit: SubmitHandler<NewTagData> = async (data: any) => {
        if(info.update) {
            let isPersonal: boolean = false;
            if (info.currentUser.role_id === RoleEnum.AGENT) {
                isPersonal = true;
            } else {
                isPersonal = data.is_personal === "1" ? true : false;
            }
        
            const editTag: NewTagData = {
                label: data.label,
                is_personal: isPersonal,
            };
        
            dispatch(
                updateTag({data: editTag, agencyId: info.currentUser.agency_id!, tagId: info.tag?.id!})
            ).then((e) => {
                if (e.type === "tags/updateTag/rejected") {
                    setShowTagError(true);
                } else {
                    toast(t("SAVED_SUCCESFULLY"));
                    closeModal();
                }
            });
        } else {
            let isPersonal: boolean = false;
            if (info.currentUser.role_id === RoleEnum.AGENT) {
                isPersonal = true;
            } else {
                isPersonal = data.is_personal === "1" ? true : false;
            }
        
            const newTag: NewTagData = {
                label: data.label,
                is_personal: isPersonal,
            };
        
            dispatch(createTag({ data: newTag, agencyId: info.currentUser.agency_id! })).then((e) => {
                if (e.type === "tags/createTag/rejected") {
                    setShowTagError(true);
                } else {
                    toast(t("SAVED_SUCCESFULLY"));
                    closeModal();
                }
            });
        }
    };
    
    const closeModal = () => {
        reset({});
        onClose();
    };

    return(
        <div>
            <Modal
                isOpen={info.show}
                onClose={closeModal}
                label={info.update ? t("UPDATE_TAG") : t("NEW_TAG")}
                onSave={handleSubmit(onSubmit, onSubmitInvalid)}
                >
                <div className="w-full flex flex-wrap">
                    {(info.currentUser && (info.currentUser.role_id !== RoleEnum.AGENT)) && (
                        <div className="w-full flex flex-wrap">
                            <ReactiveFormInput
                            control={control}
                            className="md:w-1/2"
                            label={t("LABEL")}
                            name="label"
                            isRequired
                            />
                            <ReactiveFormRadioButtonSelect
                                className={"w-full px-3 md:w-1/2"}
                                control={control}
                                name="is_personal"
                                label={t("IS_PERSONAL_TAG")}
                                options={[
                                    { label: t("YES"), value: "1" },
                                    { label: t("NO"), value: "0" },
                                ]}
                            />
                        </div>
                    )}
                    {(info.currentUser && info.currentUser.role_id === RoleEnum.AGENT) && (
                        <div className="w-full">
                            <ReactiveFormInput
                            control={control}
                            className="md:w-1/1"
                            label={t("LABEL")}
                            name="label"
                            isRequired
                            />
                        </div>
                    )}
                </div>
            </Modal>
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            {isLoading && <LoadingMask/>}
        </div>
    )
}